import React, { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "../../styles/components/ContactModals.module.scss";
import { InlineWidget } from "react-calendly";

export function useContact(onSubmit) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("sheet");
  const [active, setActive] = useState(null);

  const openSheet = () => {
    setOpen(true);
    setView("sheet");
    setActive(null);
  };

  const openCard = (id) => {
    setOpen(true);
    setView("form");
    setActive(id);
  };

  const openCalendly = () => {
    setOpen(true);
    setView("calendly");
    setActive(null);
  };

  const close = () => {
    setOpen(false);
    setView("sheet");
    setActive(null);
  };

  const Modal = () =>
    open ? (
      <ContactModal
        view={view}
        active={active}
        onClose={close}
        onBack={() => setView("sheet")}
        onPick={(id) => {
          setActive(id);
          setView("form");
        }}
        onSubmit={onSubmit}
      />
    ) : null;

  // IMPORTANT: return openCalendly
  return { openSheet, openCard, openCalendly, Modal };
}

/* -------------------------------------------------------------------------- */
/*                                INTERNAL UI                                 */
/* -------------------------------------------------------------------------- */

const DEFAULT_EMAIL = "eklektoneto@gmail.com";

// Calendly event link:
const CALENDLY_URL = "https://calendly.com/muntazaworkspace/new-meeting";

const FORM_DEFS = {
  conversation: {
    title: "Book a conversation",
    blurb: "Share a bit of context and I’ll reply with next steps.",
    fields: [
      ["name", "Your name", "text", true],
      ["email", "Email", "email", true],
      ["org", "Organization (optional)", "text", false],
      ["notes", "Notes / context", "textarea", true],
    ],
    cta: "Send request",
    subject: "Conversation request",
  },

  speaking: {
    title: "Speaking & workshops",
    blurb: "Share event details and what you’d like covered. I’ll respond with availability.",
    fields: [
      ["name", "Your name", "text", true],
      ["email", "Email", "email", true],
      ["org", "Organization", "text", true],
      ["event", "Event name / venue", "text", true],
      ["date", "Event date (optional)", "text", false],
      ["format", "Format (keynote, panel, workshop)", "text", true],
      ["topic", "Topic / theme", "text", true],
      ["audience", "Audience size (optional)", "text", false],
      ["notes", "Anything else (optional)", "textarea", false],
    ],
    cta: "Send request",
    subject: "Speaking / workshop request",
    thanksTitle: "Thanks — speaking request received.",
    thanksBody: "I’ll reply by email with availability and next steps.",
  },

  services: {
    title: "Services",
    blurb: "Tell me what you need help with and your timeline. I’ll respond with options.",
    fields: [
      ["name", "Your name", "text", true],
      ["email", "Email", "email", true],
      ["org", "Organization", "text", true],
      ["need", "What do you need support with?", "textarea", true],
      ["timeline", "Timeline (optional)", "text", false],
      ["budget", "Budget range (optional)", "text", false],
      ["links", "Links (optional)", "text", false],
    ],
    cta: "Send inquiry",
    subject: "Service inquiry",
    thanksTitle: "Thanks — I got your service inquiry.",
    thanksBody: "I’ll review your message and reply by email with next steps.",
  },

  collaboration: {
    title: "Collaboration / Partnerships",
    blurb: "For collaborations, partnerships, or community-driven initiatives.",
    fields: [
      ["name", "Your name", "text", true],
      ["email", "Email", "email", true],
      ["org", "Organization (optional)", "text", false],
      ["about", "What are you working on?", "textarea", true],
      ["ask", "How would you like to collaborate?", "textarea", true],
      ["links", "Links (optional)", "text", false],
    ],
    cta: "Send request",
    subject: "Collaboration request",
    thanksTitle: "Thanks — your collaboration request is in.",
    thanksBody: "I’ll take a look and get back to you by email soon.",
  },

  nonprofit: {
    title: "Nonprofit technical support",
    blurb: "Support for mission-driven organizations (lightweight, practical, and scalable).",
    fields: [
      ["name", "Your name", "text", true],
      ["email", "Email", "email", true],
      ["org", "Nonprofit / initiative name", "text", true],
      ["mission", "Mission (1–2 sentences)", "textarea", true],
      ["need", "What technical support do you need?", "textarea", true],
      ["urgency", "Urgency (optional)", "text", false],
      ["links", "Website / links (optional)", "text", false],
    ],
    cta: "Request support",
    subject: "Nonprofit technical support request",
    thanksTitle: "Thanks — I received your nonprofit support request.",
    thanksBody: "I’ll reply by email after reviewing your needs and context.",
  },
};

function CalendlyCard({ onBack, onClose }) {
  return (
    <>
      <div className={styles.hRow}>
        <div className={styles.breadcrumb}>
          <button className={styles.link} onClick={onBack} aria-label="Back to options">
            Ways to work together
          </button>
          <span className={styles.sep}>/</span>
          <span className={styles.bcCurrent}>Book a conversation</span>
        </div>
        <button className={styles.iconBtn} onClick={onClose} aria-label="Close">
          ×
        </button>
      </div>

      <h3 className={styles.cardTitle}>Book a conversation</h3>
      <p className={styles.cardBlurb}>Pick a time that works for you.</p>

      {/*Inline Calendly scheduler */}
      <div style={{ height: 640, borderRadius: 16, overflow: "hidden" }}>
        <InlineWidget url={CALENDLY_URL} />
      </div>
    </>
  );
}

function ContactModal({ view, active, onClose, onBack, onPick, onSubmit }) {
  const root = typeof document !== "undefined" ? document.body : null;

  const sheetItems = [
    ["services", "Services", "Product / engineering / research support"],
    ["collaboration", "Collaboration / Partnerships", "Community-driven initiatives & partnerships"],
    ["nonprofit", "Nonprofit technical support", "Practical help for mission-driven orgs"],
  ];

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => (document.body.style.overflow = prev);
  }, []);

  const panelRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Tab" && panelRef.current) {
        const f = panelRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!f.length) return;
        const first = f[0];
        const last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!root) return null;

  const stop = (e) => e.stopPropagation();
  const safeKey = active && FORM_DEFS[active] ? active : "services";

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.center} onClick={stop}>
        <div className={styles.panel} ref={panelRef} role="dialog" aria-modal="true">
          {view === "sheet" ? (
            <Sheet items={sheetItems} onPick={onPick} onClose={onClose} />
          ) : view === "calendly" ? (
            <CalendlyCard onBack={onBack} onClose={onClose} />
          ) : (
            <FormCard
              path={safeKey}
              def={FORM_DEFS[safeKey]}
              onBack={onBack}
              onClose={onClose}
              onSubmit={onSubmit}
            />
          )}
        </div>
      </div>
    </div>,
    root
  );
}

/* ------------------------------ Sheet (list) ------------------------------ */

function Sheet({ items, onPick, onClose }) {
  return (
    <>
      <div className={styles.hRow}>
        <h3 className={styles.h}>Ways to work together</h3>
        <button className={styles.iconBtn} onClick={onClose} aria-label="Close">
          ×
        </button>
      </div>

      <ul className={styles.list} role="menu" aria-label="Contact options">
        {items.map(([id, title, sub]) => (
          <li key={id}>
            <button className={styles.item} role="menuitem" onClick={() => onPick(id)}>
              <div>
                <div className={styles.itemTitle}>{title}</div>
                <div className={styles.itemSub}>{sub}</div>
              </div>
              <span className={styles.chev} aria-hidden>
                ›
              </span>
            </button>
          </li>
        ))}
      </ul>
    </>
  );
}

/* ------------------------------ Form (card) ------------------------------- */

function reducer(state, action) {
  switch (action.type) {
    case "SET":
      return { ...state, [action.name]: action.value };
    case "RESET":
      return action.initial;
    default:
      return state;
  }
}

function FormCard({ path, def, onBack, onClose, onSubmit }) {
  const initial = useMemo(() => {
    const base = {};
    def.fields.forEach(([name]) => (base[name] = ""));
    return base;
  }, [def.fields]);

  const [data, dispatch] = useReducer(reducer, initial);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [statusMsg, setStatusMsg] = useState("");

  const set = (name, value) => dispatch({ type: "SET", name, value });

  function resetForm() {
    dispatch({ type: "RESET", initial });
    setErrors({});
    setStatus("idle");
    setStatusMsg("");
  }

  function validate() {
    const next = {};
    def.fields.forEach(([name, _label, _type, req]) => {
      if (req && !String(data[name] || "").trim()) next[name] = "Required";
      if (name === "email" && data[name] && !/.+@.+\..+/.test(data[name])) {
        next[name] = "Enter a valid email";
      }
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function mailtoFallback() {
    const subject = encodeURIComponent(def.subject || "New message");
    const lines = Object.entries(data)
      .filter(([, v]) => String(v || "").trim())
      .map(([k, v]) => `${labelFor(def, k)}: ${v}`);

    const body = encodeURIComponent(lines.join("\n"));
    window.location.href = `mailto:${DEFAULT_EMAIL}?subject=${subject}&body=${body}`;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setStatusMsg("");

    if (!validate()) return;

    try {
      setStatus("sending");
      if (typeof onSubmit === "function") {
        await onSubmit({ path, data, def });
        setStatus("sent");
      } else {
        mailtoFallback();
        setStatus("sent");
        setStatusMsg("Opening your email app…");
      }
    } catch (err) {
      setStatus("error");
      setStatusMsg(err?.message || "Could not send. Try again.");
    }
  }

  if (status === "sent") {
    return (
      <>
        <div className={styles.hRow}>
          <div className={styles.breadcrumb}>
            <button className={styles.link} onClick={onBack} aria-label="Back to options">
              Ways to work together
            </button>
            <span className={styles.sep}>/</span>
            <span className={styles.bcCurrent}>{def.title}</span>
          </div>
          <button className={styles.iconBtn} onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <h3 className={styles.cardTitle}>{def.thanksTitle || "Thanks — message sent."}</h3>
        <p className={styles.cardBlurb}>{def.thanksBody || "I’ll reply by email as soon as I can."}</p>

        <div className={styles.actions} style={{ marginTop: 18 }}>
          <button type="button" className={styles.ghost} onClick={resetForm}>
            Send another
          </button>
          <button type="button" className={styles.primary} onClick={onClose}>
            Close
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className={styles.hRow}>
        <div className={styles.breadcrumb}>
          <button className={styles.link} onClick={onBack} aria-label="Back to options">
            Ways to work together
          </button>
          <span className={styles.sep}>/</span>
          <span className={styles.bcCurrent}>{def.title}</span>
        </div>
        <button className={styles.iconBtn} onClick={onClose} aria-label="Close">
          ×
        </button>
      </div>

      <h3 className={styles.cardTitle}>{def.title}</h3>
      <p className={styles.cardBlurb}>{def.blurb}</p>

      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        {def.fields.map(([name, label, type]) => (
          <Field
            key={name}
            name={name}
            label={label}
            type={type}
            value={data[name]}
            onChange={(v) => set(name, v)}
            error={errors[name]}
          />
        ))}

        <div className={styles.actions}>
          <button type="button" className={styles.ghost} onClick={onBack} disabled={status === "sending"}>
            Back
          </button>
          <button type="submit" className={styles.primary} disabled={status === "sending"}>
            {status === "sending" ? "Sending…" : def.cta}
          </button>
        </div>

        {statusMsg ? (
          <div
            className={status === "error" ? styles.statusError : styles.statusOk}
            role="status"
            aria-live="polite"
            style={{ marginTop: 12 }}
          >
            {statusMsg}
          </div>
        ) : null}
      </form>
    </>
  );
}

function labelFor(def, key) {
  const f = def.fields.find(([name]) => name === key);
  return f ? f[1] : key;
}

function Field({ name, label, type, value, onChange, error }) {
  const id = `f-${name}`;

  if (type === "textarea") {
    return (
      <div className={styles.f}>
        <label htmlFor={id}>{label}</label>
        <textarea id={id} value={value} onChange={(e) => onChange(e.target.value)} rows={4} />
        {error && <div className={styles.err}>{error}</div>}
      </div>
    );
  }

  return (
    <div className={styles.f}>
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={name === "email" ? "email" : "on"}
      />
      {error && <div className={styles.err}>{error}</div>}
    </div>
  );
}
